import React from "react";
import { Link } from "react-router-dom";


const ApiDocs = () => {
  return (
    <div className="max-w-4xl mx-auto py-10 px-6 text-gray-800">
      <h1 className="text-3xl font-bold text-teal-600 mb-6">IBAN Analyzer API Documentation</h1>

      {/* Intro */}
      <p className="mb-8">
        Our API allows you to validate IBANs, analyze bank information, and explore supported countries and banks. Each authenticated request must include an API key in the header.
      </p>

      <p className="bg-teal-50 text-teal-800 border border-teal-200 rounded-md p-4 text-sm mb-6">
        🎯 Want to try what the API can do without writing code? 
        Head over to the <a href="/" className="underline text-teal-600">main IBAN Analyzer site</a> and test IBANs interactively. 
        It uses the same backend as this API.
      </p>


      {/* Endpoint Overview */}
      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">📚 Overview</h2>
        <ul className="list-disc list-inside space-y-2">
          <li>
            <strong>POST /api/v1/validate</strong> — Validate one or more IBANs. <a href="#validate" className="text-teal-600 underline">See details</a>
          </li>
          <li>
            <strong>POST /api/v1/analyze</strong> — Validate and extract detailed info from one or more IBANs. <a href="#analyze" className="text-teal-600 underline">See details</a>
          </li>
          <li>
            <strong>GET /api/v1/countries</strong> — Get list of supported IBAN countries. <a href="#countries" className="text-teal-600 underline">See details</a>
          </li>
          <li>
            <strong>GET /api/v1/countries/:country_code</strong> — Get detailed info for a specific country. <a href="#country-detail" className="text-teal-600 underline">See details</a>
          </li>
          <li>
            <strong>GET /api/v1/banks/:country_code</strong> — Get all supported banks for a country. <a href="#banks" className="text-teal-600 underline">See details</a>
          </li>
        </ul>
      </section>

      {/* Authentication */}
      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-2">🔐 Authentication</h2>
        <p className="mb-2">
          All endpoints require an API key passed via the <code className="bg-gray-100 px-1">X-Api-Key</code> header.
        </p>
        <p className="mb-2">
            You can find your API key in your{" "}
            <Link to="/dashboard" className="text-teal-600 underline">dashboard</Link>.
        </p>

        <pre className="mb-2 font-mono bg-gray-100 p-3 rounded text-sm overflow-x-auto">
{`curl -X POST https://iban-analyzer.com/api/v1/validate \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: YOUR_API_KEY" \\
  -d '{"iban": "HR1210010051863000160"}'`}
</pre>
      </section>

      {/* Validate */}
      <section id="validate" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2">✅ Validate IBANs</h2>
        <p>
          Validates one or multiple IBANs. This request contributes to your IBAN validation credit usage.
          Use this endpoint when you only need to verify the structural correctness of an IBAN and not retrieve bank or country details.
        </p>

        <p className="mt-4 font-semibold">Request</p>
        <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
      {`POST /api/v1/validate
      Content-Type: application/json
      X-Api-Key: YOUR_API_KEY

      Body:
      { "iban": "HR1210010051863000160" }

      or

      ["HR1210010051863000160", "DE89370400440532013000"]`}
        </pre>

        <p className="mt-4 font-semibold">Response</p>
        <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
      {`{
        "results": [
          {
            "iban": "HR1210010051863000160",
            "isValid": true,
            "validationBreakdown": {
              "ibanCheckDigitValid": true,
              "ibanMod97Valid": true,
              "lengthValid": true,
              "structureValid": true,
              "overallValid": true
            }
          }
        ]
      }`}
        </pre>

        <p className="mt-4 font-semibold">Response Fields</p>
        <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
          <li>
            <code>iban</code>: The input IBAN string after formatting (spaces removed, uppercase).
          </li>
          <li>
            <code>isValid</code>: Overall result of all validation checks. Will only be <code>true</code> if all checks in <code>validationBreakdown</code> pass.
          </li>
          <li>
            <code>validationBreakdown</code>: An object describing the result of each individual validation step.
          </li>
        </ul>

        <p className="mt-4 font-semibold">validationBreakdown Fields</p>
        <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
          <li>
            <code>ibanCheckDigitValid</code>: Confirms that the check digits (characters 3–4 of the IBAN) are correct, computed according to ISO 7064 Mod 97-10.
          </li>
          <li>
            <code>ibanMod97Valid</code>: Validates the full IBAN using Mod 97 after rearranging the IBAN and converting letters to numbers.
          </li>
          <li>
            <code>lengthValid</code>: Checks if the IBAN’s length matches the expected number of characters for its country code.
          </li>
          <li>
            <code>structureValid</code>: Confirms the IBAN structure is supported and extractable for that country.
          </li>
          <li>
            <code>overallValid</code>: Will be <code>true</code> only if all the above checks return true.
          </li>
        </ul>
      </section>

      {/* Analyze */}
      <section id="analyze" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2">🔍 Analyze IBANs</h2>
        <p>
          Validates and analyzes IBAN structure, country info, and bank info.
          This request contributes to your bank info credit usage.
        </p>

        <p className="mt-4 font-semibold">Request</p>
        <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
      {`POST /api/v1/analyze
      Content-Type: application/json
      X-Api-Key: YOUR_API_KEY

      Body:
      { "iban": "HR3124020063211094260" }`}
        </pre>

        <p className="mt-4 font-semibold">Response</p>
        <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
      {`{
        "results": [
          {
            "iban": "HR3124020063211094260",
            "isValid": true,
            "validationBreakdown": {
              "ibanCheckDigitValid": true,
              "ibanMod97Valid": true,
              "lengthValid": true,
              "structureValid": true,
              "overallValid": true
            },
            "ibanDetails": {
              "iban": "HR3124020063211094260",
              "countryCode": "HR",
              "bankCode": "2402006",
              "accountNumber": "3211094260"
            },
            "countryInfo": {
              "code": "HR",
              "name": "Croatia",
              "sepaSupport": true
            },
            "bankDetails": {
              "bankName": "Erste & Steiermärkische Bank",
              "swiftCode": "ESBCHR22",
              "bankCity": "Rijeka",
              "bankAddress": "Jadranski trg 3a",
              "bankZipCode": "51000"
            }
          }
        ]
      }`}
        </pre>

        <p className="mt-4 font-semibold">Validation Breakdown Fields</p>
        <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
          <li>
            <code>ibanCheckDigitValid</code>: Verifies that the two check digits (characters 3–4 of the IBAN) match the expected value based on ISO 7064 Mod 97-10.
          </li>
          <li>
            <code>ibanMod97Valid</code>: Confirms that the IBAN passes the Mod 97 operation by rearranging the IBAN and converting letters to numbers.
          </li>
          <li>
            <code>lengthValid</code>: Checks that the IBAN length matches the expected number of characters for the country (e.g., 21 for HR, 22 for DE).
          </li>
          <li>
            <code>structureValid</code>: Confirms that the IBAN structure is supported for the country, and that it includes valid bank/account component extraction rules.
          </li>
          <li>
            <code>overallValid</code>: Returns <code>true</code> only if all the above checks passed.
          </li>
        </ul>

        <p className="mt-6 font-semibold">IBAN Details Fields</p>
        <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
          <li><code>iban</code>: The cleaned full IBAN (no spaces, uppercase).</li>
          <li><code>countryCode</code>: Two-letter ISO country code (e.g. <code>HR</code> for Croatia).</li>
          <li><code>bankCode</code>: Bank identifier extracted according to the country’s IBAN structure.</li>
          <li><code>accountNumber</code>: Account number part of the IBAN, extracted according to the national structure.</li>
        </ul>

        <p className="mt-6 font-semibold">Country Info Fields</p>
        <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
          <li><code>code</code>: ISO 2-letter country code.</li>
          <li><code>name</code>: Human-readable country name.</li>
          <li><code>sepaSupport</code>: Indicates whether the country participates in SEPA (Single Euro Payments Area).</li>
        </ul>

        <p className="mt-6 font-semibold">Bank Details Fields</p>
        <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
          <li><code>bankName</code>: Official name of the bank.</li>
          <li><code>swiftCode</code>: The bank’s SWIFT/BIC code.</li>
          <li><code>bankCity</code>: City where the bank is located.</li>
          <li><code>bankAddress</code>: Street address of the bank’s headquarters.</li>
          <li><code>bankZipCode</code>: Postal code for the bank’s address.</li>
        </ul>
      </section>

      {/* Countries */}
      <section id="countries" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2">🌍 Supported Countries</h2>
        <p>Returns a list of countries with IBAN validation and bank info support flags.</p>

        <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
{`GET /api/v1/countries
X-Api-Key: YOUR_API_KEY`}
        </pre>

        <p className="mt-4 font-semibold">Response</p>
        <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
{`[
  {
    "code": "HR",
    "name": "Croatia",
    "ibanValidationSupported": true,
    "bankInfoSupported": true
  },
  ...
]`}
        </pre>
      </section>

      {/* Country Detail */}
    <section id="country-detail" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2">📌 Country Info</h2>
        <p>Returns detailed IBAN specification for a specific country.</p>

        <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
        {`GET /api/v1/countries/GB
        X-Api-Key: YOUR_API_KEY`}
        </pre>

        <p className="mt-4 font-semibold">Response</p>
        <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
        {`{
        "code": "GB",
        "name": "United Kingdom",
        "ibanLength": 22,
        "ibanApprovedForIntlUse": true,
        "sepaSupport": true
        }`}
        </pre>

        {/* 📝 Remarks */}
        <ul className="text-sm text-gray-700 mt-4 list-disc list-inside">
            <li>
            <strong>ibanApprovedForIntlUse</strong>: Indicates that the IBAN format is officially recognized and standardized for this country in international transactions.
            </li>
            <li>
            <strong>sepaSupport</strong>: Specifies whether the country is part of the SEPA (Single Euro Payments Area), enabling euro bank transfers with reduced fees and faster processing.
            </li>
        </ul>
    </section>

      {/* Banks */}
      <section id="banks" className="mb-10">
        <h2 className="text-2xl font-semibold mb-2">🏦 Banks in a Country</h2>
        <p>Returns all supported banks for a country with details.</p>

        <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
{`GET /api/v1/banks/HR
X-Api-Key: YOUR_API_KEY`}
        </pre>

        <p className="mt-4 font-semibold">Response</p>
        <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto max-h-96 overflow-y-scroll">
{`{
  "banks": [
    {
      "bankName": "Erste & Steiermärkische Bank",
      "swiftCode": "ESBCHR22",
      "bankCity": "Rijeka",
      "bankAddress": "Jadranski trg 3a",
      "bankZipCode": "51000"
    },
    ...
  ]
}`}
        </pre>
      </section>

      {/* Quota Usage Logic */}
      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-2">📈 Quota Usage Logic</h2>
        <p className="mb-4">
          To make our service more user-friendly and fair, we apply the following rules when counting your credit usage:
        </p>

        <ul className="list-disc list-inside text-sm text-gray-700 space-y-2">
          <li>
            <strong>Validation Requests</strong> (<code>/api/v1/validate</code> and IBAN validation inside <code>/api/v1/analyze</code>):
            Each request consumes 1 IBAN validation credit, <strong>even if the IBAN is invalid</strong>.
          </li>
          <li>
            <strong>Bank Information Requests</strong> (<code>/api/v1/analyze</code>):
            Credits are <strong>only deducted if a valid IBAN with matching bank information is found</strong>.
          </li>
          <li>
            If the IBAN is valid but we have no bank information for it, <strong>no bank information credit is deducted</strong>.
          </li>
          <li>
            If all IBANs in the request are invalid, <strong>only a validation credit is deducted</strong>, not a bank info credit.
          </li>
        </ul>

        <p className="mt-4 text-sm text-gray-600">
          This way, you are only charged for successful validations or when valuable bank information is returned.
        </p>
      </section>

      {/* Errors */}
      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-2">🚫 Error Responses</h2>
        <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
{`401 Unauthorized
{
  "error": "Missing or invalid API key"
}

403 Forbidden
{
  "error": "Quota exceeded"
}

400 Bad Request
{
  "error": "No IBANs provided"
}`}
        </pre>
      </section>
    </div>
  );
};

export default ApiDocs;